import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Input } from './input.js';
import { OptionListbox } from './option-listbox.js';
import { Popover } from './popover.js';
import { RemovableBadge } from './removable-badge.js';
const DEFAULT_MAX_TAGS = 20;
const DEFAULT_PLACEHOLDER = 'Добавить тег…';
function defaultCreateLabel(q) {
    return `Создать «${q}»`;
}
/** Ключ сравнения имён: теги различаются смыслом, а не регистром и не пробелами по краям. */
function norm(name) {
    return name.trim().toLocaleLowerCase('ru-RU');
}
/**
 * Заголовок секции перед `items[index]` — если с него начинается новая группа
 * (`TagSuggestion.group`); иначе null. Не пункт списка: `role="presentation"`.
 */
function groupHeaderAt(items, index) {
    const item = items[index];
    const group = item?.kind === 'suggestion' ? item.source.group : undefined;
    if (group === undefined || group === '')
        return null;
    const prev = index > 0 ? items[index - 1] : undefined;
    if (prev?.kind === 'suggestion' && prev.source.group === group)
        return null;
    return (_jsx("li", { role: "presentation", className: "px-2 pt-2 pb-1 text-xs font-medium text-[var(--color-text-muted)] first:pt-1", children: group }, `group:${group}:${index}`));
}
/**
 * Поле ввода тегов: выбранные — съёмные чипы, ввод открывает список подсказок,
 * недостающий тег создаётся прямо отсюда.
 *
 * ПОЛНОСТЬЮ КОНТРОЛИРУЕМЫЙ и БЕЗ ДОМЕНА: ни `fetch`, ни знания о том, что такое
 * «тег диалога». Наверх уходят два события — «запрос изменился» и «набор
 * изменился», — а поиск и запись делает родитель. Так один и тот же виджет
 * годится и рейлу диалога, и массовой простановке, и фильтру, а тесты не
 * зависят от сети.
 *
 * Подсказки НЕ фильтруются по подстроке здесь: сервер уже ответил на `q`, и
 * второй фильтр поверх него только прятал бы то, что сервер счёл подходящим
 * (нечёткое совпадение, синоним). Скрывается ровно одно — уже выбранное.
 */
export function TagInput({ value, identity = 'name', getLabel, renderValue, renderSuggestion, suggestions, query, onQueryChange, onChange, loading = false, disabled = false, placeholder = DEFAULT_PLACEHOLDER, maxTags = DEFAULT_MAX_TAGS, maxLength, createLabel = defaultCreateLabel, allowCreate = true, emptyLabel = 'Ничего не найдено', 'aria-label': ariaLabel = 'Теги', id, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, 'aria-invalid': ariaInvalid, onBlur, inputRef, onClear, indicator = false, listFooter, maxLabel, multiselectable = false, removeLabel = (label) => `Убрать тег ${label}`, className, }) {
    const [open, setOpen] = React.useState(false);
    const [highlight, setHighlight] = React.useState(0);
    const fieldRef = React.useRef(null);
    const ownInputRef = React.useRef(null);
    const setInputRef = React.useCallback((el) => {
        ownInputRef.current = el;
        if (typeof inputRef === 'function')
            inputRef(el);
        else if (inputRef)
            inputRef.current = el;
    }, [inputRef]);
    const baseId = React.useId();
    const listboxId = `${baseId}-listbox`;
    const optionId = (index) => `${baseId}-option-${index}`;
    const trimmed = query.trim();
    const atMax = value.length >= maxTags;
    const tooLong = maxLength != null && trimmed.length > maxLength;
    const byId = identity === 'id';
    // Ключ сравнения выбранного: имя — без регистра и краевых пробелов, id — как есть.
    const keyOf = React.useCallback((v) => (byId ? v : norm(v)), [byId]);
    const selected = React.useMemo(() => new Set(value.map(keyOf)), [value, keyOf]);
    const labelOf = (v) => (byId ? (getLabel?.(v) ?? v) : v);
    // Личность массива `suggestions` меняется на каждый рендер родителя, поэтому
    // сбрасывать подсветку по ней нельзя — она гасила бы наведение мышью. Ключ по
    // содержимому меняется только тогда, когда справочник реально ответил другим.
    const suggestionsKey = `${suggestions.length}:${suggestions.map((s) => s.id).join('|')}`;
    // Новый запрос или новая выдача — новый список, и подсветка возвращается на
    // первый пункт. Без сброса по выдаче поздний ответ сервера подменяет пункт под
    // подсветкой, и Enter добавляет не тот тег, на который смотрит оператор.
    React.useEffect(() => {
        setHighlight(0);
    }, [query, suggestionsKey]);
    const items = React.useMemo(() => {
        const available = suggestions
            .map((s) => ({
            kind: 'suggestion',
            value: byId ? s.id : s.name,
            name: s.name,
            usageCount: s.usageCount,
            source: s,
            disabled: s.disabled === true,
        }))
            .filter((s) => !selected.has(keyOf(s.value)));
        const canCreate = !byId &&
            allowCreate &&
            trimmed.length > 0 &&
            !tooLong &&
            !selected.has(norm(trimmed)) &&
            !suggestions.some((s) => norm(s.name) === norm(trimmed));
        return canCreate
            ? [...available, { kind: 'create', value: trimmed, name: trimmed }]
            : available;
    }, [suggestions, selected, trimmed, tooLong, allowCreate, byId, keyOf]);
    // Список показываем, только когда в нём есть что читать: пустая карточка под
    // полем — шум, а не подсказка. На пределе по тегам список не открываем вовсе —
    // предлагать то, что нельзя добавить, хуже, чем не предлагать ничего.
    const hasSomethingToShow = loading || items.length > 0 || trimmed.length > 0;
    const isOpen = open && hasSomethingToShow && !disabled && !atMax;
    /**
     * ЕДИНСТВЕННЫЙ признак «список реально отрисован». В состояниях «Загрузка…» и
     * «Ничего не найдено» карточка есть, а `<ul>` нет — и всё, что адресует пункты
     * (`aria-controls`, `aria-activedescendant`, ↑/↓, Enter), обязано молчать.
     * Иначе клавиатура ходит по невидимому списку: `items` при `loading` ещё
     * хранит прошлую выдачу, и Enter добавил бы тег, которого оператор не видел.
     */
    const navigable = isOpen && !loading && items.some((i) => !i.disabled);
    const active = navigable ? firstEnabledFrom(items, Math.min(highlight, items.length - 1), 1) : -1;
    function addTag(raw) {
        // id — непрозрачный идентификатор: ни обрезки, ни предела длины имени.
        const next = byId ? raw : raw.trim();
        if (!next || value.length >= maxTags)
            return;
        if (!byId && maxLength != null && next.length > maxLength)
            return;
        if (!selected.has(keyOf(next)))
            onChange([...value, next]);
        onQueryChange('');
        setHighlight(0);
    }
    function handleKeyDown(event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            // Закрытый список стрелка открывает, но не двигает: двигать пока нечего.
            if (!isOpen) {
                setOpen(true);
                return;
            }
            if (!navigable)
                return;
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setHighlight(() => {
                const from = active < 0 ? 0 : active;
                return firstEnabledFrom(items, (from + step + items.length) % items.length, step);
            });
            return;
        }
        if (event.key === 'Enter') {
            // Гасим Enter ТОЛЬКО когда есть что выбирать: поле часто живёт внутри
            // формы, и глушить чужую отправку, ничего не сделав, мы не вправе.
            if (active < 0 || items[active].disabled)
                return;
            event.preventDefault();
            addTag(items[active].value);
            return;
        }
        if (event.key === 'Backspace' && query === '' && value.length > 0) {
            event.preventDefault();
            onChange(value.slice(0, -1));
            return;
        }
        if (event.key === 'Escape')
            setOpen(false);
    }
    const showClear = onClear != null && value.length > 0 && !disabled;
    return (_jsxs("div", { className: cn('flex flex-col gap-1', className), children: [_jsxs("div", { ref: fieldRef, 
                // Клик по пустому месту строки (между чипами) — фокус в поле и список:
                // строка выглядит как одно поле и обязана так и работать.
                onMouseDown: (event) => {
                    if (disabled)
                        return;
                    const target = event.target;
                    // Список — в портале, но события React всплывают и из него: по DOM
                    // он вне строки, и его клики здесь не обрабатываются.
                    if (!fieldRef.current?.contains(target))
                        return;
                    if (target === ownInputRef.current || target.closest('button'))
                        return;
                    event.preventDefault();
                    ownInputRef.current?.focus();
                    setOpen(true);
                }, className: cn('flex flex-wrap items-center gap-1.5 rounded-[var(--radius-2)] border border-[var(--color-border-default)] bg-[var(--color-surface-canvas)] px-2 py-1.5', 'focus-within:ring-2 focus-within:ring-[var(--color-border-focus)]', disabled && 'opacity-50'), children: [value.map((v, index) => (_jsx(RemovableBadge, { removeAriaLabel: removeLabel(labelOf(v)), onRemove: () => onChange(value.filter((_, i) => i !== index)), disabled: disabled, children: renderValue ? renderValue(v) : labelOf(v) }, `${v}-${index}`))), _jsxs(Popover, { open: isOpen, onOpenChange: setOpen, children: [_jsx(Popover.Anchor, { asChild: true, children: _jsx("div", { className: "min-w-32 flex-1", children: _jsx(Input, { ref: setInputRef, id: id, role: "combobox", "aria-label": ariaLabelledBy ? undefined : ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, "aria-expanded": isOpen, "aria-controls": navigable ? listboxId : undefined, "aria-haspopup": "listbox", "aria-autocomplete": "list", "aria-activedescendant": active >= 0 ? optionId(active) : undefined, value: query, maxLength: maxLength, placeholder: atMax ? undefined : placeholder, 
                                        // На пределе поле НЕ блокируется: заблокированное поле забирает
                                        // вместе с вводом и Backspace, и снять лишний тег с клавиатуры
                                        // становится нечем. Добавление глушится в `addTag`.
                                        disabled: disabled, onChange: (event) => {
                                            onQueryChange(event.target.value);
                                            setOpen(true);
                                        }, onFocus: () => setOpen(true), 
                                        // Клик по УЖЕ сфокусированному полю не рождает `focus`, поэтому
                                        // без этого список, закрытый по Esc, назад не открыть.
                                        onMouseDown: () => setOpen(true), onKeyDown: handleKeyDown, onBlur: onBlur, className: "h-7 border-0 bg-transparent px-1 focus-visible:ring-0" }) }) }), _jsxs(Popover.Content, { align: "start", 
                                // Фокус обязан остаться в поле: список — продолжение ввода, а не
                                // отдельный диалог. Без этого Radix уводит фокус на карточку, и
                                // следующая буква улетает в никуда.
                                onOpenAutoFocus: (event) => event.preventDefault(), onCloseAutoFocus: (event) => event.preventDefault(), 
                                // Поле и чипы лежат СНАРУЖИ карточки, поэтому для `DismissableLayer`
                                // клик по собственному полю — «клик наружу», и список закрывался бы
                                // прямо под курсором. Гасим закрытие только для своей же зоны; всё,
                                // что вне неё, закрывает список как обычно.
                                onInteractOutside: (event) => {
                                    if (fieldRef.current?.contains(event.target))
                                        event.preventDefault();
                                }, className: "w-[var(--radix-popover-trigger-width)] min-w-56 p-1", children: [loading ? (_jsx("div", { className: "px-2 py-1.5 text-sm text-[var(--color-text-muted)]", children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026" })) : items.length === 0 ? (_jsx("div", { className: "px-2 py-1.5 text-sm text-[var(--color-text-muted)]", children: emptyLabel })) : (_jsx(OptionListbox, { id: listboxId, "aria-label": ariaLabel, "aria-multiselectable": multiselectable, activeOptionId: active >= 0 ? optionId(active) : undefined, children: items.map((item, index) => [
                                            groupHeaderAt(items, index),
                                            _jsxs("li", { id: optionId(index), role: "option", "aria-selected": false, "data-highlighted": index === active ? '' : undefined, "aria-disabled": item.disabled ? true : undefined, 
                                                // Мышь не должна забирать фокус у поля до того, как клик
                                                // отработает, — иначе список успевает закрыться.
                                                onMouseDown: (event) => event.preventDefault(), onMouseEnter: () => {
                                                    if (!item.disabled)
                                                        setHighlight(index);
                                                }, onClick: () => {
                                                    if (!item.disabled)
                                                        addTag(item.value);
                                                }, className: cn('flex items-center justify-between gap-2 rounded-[var(--radius-2)] px-2 py-1.5 text-sm', item.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer', index === active
                                                    ? 'bg-[var(--color-surface-sunken)] text-[var(--color-text-primary)]'
                                                    : 'text-[var(--color-text-secondary)]'), children: [item.kind === 'suggestion' && renderSuggestion ? (renderSuggestion(item.source)) : (_jsx("span", { className: "truncate", children: item.kind === 'create' ? createLabel(item.name) : item.name })), item.kind === 'suggestion' && !renderSuggestion && item.usageCount != null ? (_jsx("span", { className: "shrink-0 text-xs text-[var(--color-text-muted)]", children: item.usageCount })) : null] }, item.kind === 'create' ? `create:${item.value}` : `tag:${item.value}`),
                                        ]) })), listFooter != null && !loading ? (_jsx("div", { className: "border-t border-[var(--color-border-subtle)] px-2 pt-1.5 pb-1 text-xs text-[var(--color-text-muted)]", children: listFooter })) : null] })] }), showClear || indicator ? (_jsxs("div", { className: "ml-auto flex shrink-0 items-center gap-0.5 self-center", children: [showClear ? (_jsx("button", { type: "button", "aria-label": "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C", title: "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C", onClick: () => {
                                    onClear?.();
                                    onQueryChange('');
                                    ownInputRef.current?.focus();
                                }, className: "inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-2)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-text-primary)]", children: _jsx(X, { "aria-hidden": true, className: "h-3.5 w-3.5" }) })) : null, indicator ? (_jsx("span", { "aria-hidden": true, "data-testid": "tag-input-indicator", onMouseDown: (event) => {
                                    if (disabled)
                                        return;
                                    event.preventDefault();
                                    event.stopPropagation();
                                    ownInputRef.current?.focus();
                                    setOpen((o) => !o);
                                }, className: "inline-flex h-6 w-6 cursor-pointer items-center justify-center text-[var(--color-text-muted)]", children: _jsx(ChevronDown, { className: cn('h-4 w-4 transition-transform', isOpen && 'rotate-180') }) })) : null] })) : null] }), atMax && !disabled ? (_jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: maxLabel ? maxLabel(maxTags) : `Максимум тегов: ${maxTags}` })) : null, maxLength != null && !disabled && trimmed.length >= maxLength ? (_jsxs("p", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u041D\u0435 \u0434\u043B\u0438\u043D\u043D\u0435\u0435 ", maxLength, " \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432"] })) : null] }));
}
TagInput.displayName = 'TagInput';
/**
 * Ближайший выбираемый пункт, начиная с `from` в направлении `step` (с
 * переходом через край). Все пункты недоступны — `-1`.
 */
function firstEnabledFrom(items, from, step) {
    const n = items.length;
    for (let k = 0; k < n; k += 1) {
        const i = (((from + step * k) % n) + n) % n;
        if (!items[i].disabled)
            return i;
    }
    return -1;
}
//# sourceMappingURL=tag-input.js.map